import { memo, useState, useCallback, useMemo } from 'react';
import { CaseReportAccidentPersonsList } from '@/components/casereport/CaseReportAccidentPersonsList';
import { CaseReportAccidentSummary } from '@/components/casereport/CaseReportAccidentSummary';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Dropdownlist } from '@/components/Dropdownlist';
import { App } from '@/services/app-compat';
import { CaseRpt } from '@/services/case-report-service';
import { Codes } from '@/constants/codes';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getInsecureUUID = _wg('getInsecureUUID');

function emptyAccidentPersonForm() {
  return { tempid: null as any, personId: null as any, personName: '', vehicleId: null as any, vehicleName: '', roles: [] as any[], notes: '', atFault: null as any, injured: null as any, vehDmg: null as any };
}

interface Props {
  accident: any;
  setAccident: (a: any) => void;
  involvedPersonOptions: { id: any; name: string }[];
  involvedVehicleOptions: { id: any; name: string }[];
  isReadOnly: boolean;
  officerLabel: string;
}

export const CaseReportAccidentSection = memo(function CaseReportAccidentSection({
  accident, setAccident, involvedPersonOptions, involvedVehicleOptions, isReadOnly, officerLabel,
}: Props) {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyAccidentPersonForm);

  const formatRoles = useCallback((roles: any[]) => {
    if (!roles?.length) return '';
    const personCodes = Codes.getPersonCodes?.() || [];
    return roles.map((r: any) => {
      const code = personCodes.find((c: any) => c.code === r || c.code === Number(r));
      return code?.value || r;
    }).join(', ');
  }, []);

  const openModal = useCallback((ap?: any) => {
    if (ap) {
      let roles: any[] = [];
      if (ap.npsdPerson) {
        const p = CaseRpt.currentMDTRpt.npsdConnectionsNew.people.find(
          (pp: any) => pp.id === ap.npsdPerson || pp.id === Number(ap.npsdPerson));
        if (p?.roles) roles = p.roles;
      }
      setForm({
        tempid: ap.tempid, personId: ap.npsdPerson, personName: ap.personName || '',
        vehicleId: ap.npsdVehicle, vehicleName: ap.vehicleName || '', roles,
        notes: ap.injuryNotes || '',
        atFault: ap.isAtFault ? String(ap.isAtFault) : null,
        injured: ap.isInjured ? String(ap.isInjured) : null,
        vehDmg: ap.isVehicleDamaged ? String(ap.isVehicleDamaged) : null,
      });
    } else {
      setForm(emptyAccidentPersonForm());
    }
    setShowModal(true);
  }, []);

  const saveAccidentPerson = useCallback(() => {
    if (!form.personId) { App.showPopupNotification('Person is required.', 'error'); return; }
    if (!form.vehicleId) { App.showPopupNotification('Vehicle is required.', 'error'); return; }
    if (!form.atFault) { App.showPopupNotification('At Fault status is required.', 'error'); return; }
    if (!form.injured) { App.showPopupNotification('Injured status is required.', 'error'); return; }
    if (!form.vehDmg) { App.showPopupNotification('Vehicle Damage status is required.', 'error'); return; }

    const list = [...(accident.accidentPersonList || [])];
    const dup = list.findIndex((n: any) =>
      n.npsdPerson?.toString() === form.personId.toString() && (!form.tempid || n.tempid !== form.tempid));
    if (dup > -1) { App.showPopupNotification('This person has already been added to the accident.', 'warning'); return; }

    let vehicleName = '';
    const veh = involvedVehicleOptions.find((v: any) => v.id === form.vehicleId);
    if (veh) vehicleName = veh.name;

    const entry = {
      tempid: form.tempid || getInsecureUUID(), npsdPerson: form.personId,
      personName: form.personName, npsdVehicle: form.vehicleId, vehicleName,
      injuryNotes: form.notes, isAtFault: parseInt(form.atFault),
      isInjured: parseInt(form.injured), isVehicleDamaged: parseInt(form.vehDmg),
    };

    if (form.tempid) {
      const idx = list.findIndex((a: any) => a.tempid === form.tempid);
      if (idx > -1) list[idx] = entry;
    } else {
      list.push(entry);
    }
    const updated = { ...accident, accidentPersonList: list };
    CaseRpt.currentMDTRpt.accident = updated;
    setAccident(updated);
    setShowModal(false);
    setForm(emptyAccidentPersonForm());
  }, [form, accident, involvedVehicleOptions, setAccident]);

  return (
    <>
      <div className="tab-pane case-report-details-tab" role="tabpanel" id="Accident">
        <div className="details-grid">
          <div className="details-column-left">
            <CaseReportAccidentPersonsList
              persons={accident.accidentPersonList || []}
              involvedPersons={involvedPersonOptions}
              involvedVehicles={involvedVehicleOptions}
              isReadOnly={isReadOnly}
              onChange={(list: any[]) => {
                CaseRpt.currentMDTRpt.accident.accidentPersonList = list;
                setAccident((a: any) => ({ ...a, accidentPersonList: list.slice() }));
              }}
              onAdd={() => openModal()}
              onEdit={(item: any) => openModal(item)}
            />
          </div>
          <div className="details-column-right">
            <CaseReportAccidentSummary
              accident={accident}
              isReadOnly={isReadOnly}
              officerLabel={officerLabel}
              onAccidentChange={(a: any) => { Object.assign(CaseRpt.currentMDTRpt.accident, a); setAccident({ ...a }); }}
            />
          </div>
        </div>
      </div>

      <ConfirmModal
        open={showModal}
        title={form.tempid ? 'Edit Accident Person' : 'Add Accident Person'}
        confirmText={form.tempid ? 'Update Person' : 'Add Person'}
        confirmClass="primary"
        className="accident-person-modal"
        dialogClass="kstc-dialog--form"
        onConfirm={saveAccidentPerson}
        onCancel={() => { setShowModal(false); setForm(emptyAccidentPersonForm()); }}
      >
        <div className="form-group">
          <label className="mandatory"><b>Person</b></label>
          <Dropdownlist value={form.personId} items={involvedPersonOptions}
            labelKey="name" valueKey="id" placeholder="Select person..."
            onChange={(val) => {
              const p = involvedPersonOptions.find((o: any) => o.id === val);
              let roles: any[] = [];
              if (val) {
                const conn = CaseRpt.currentMDTRpt.npsdConnectionsNew.people.find(
                  (pp: any) => pp.id === val || pp.id === Number(val));
                if (conn?.roles) roles = conn.roles;
              }
              setForm(f => ({ ...f, personId: val, personName: p?.name || '', roles }));
            }} />
        </div>
        {form.roles && form.roles.length > 0 && (
          <div className="form-group">
            <label><b>Roles</b> (Modify on Involved Tab)</label>
            <div className="roles-display">{formatRoles(form.roles)}</div>
          </div>
        )}
        <div className="form-group">
          <label className="mandatory"><b>Vehicle</b></label>
          <Dropdownlist value={form.vehicleId} items={involvedVehicleOptions}
            labelKey="name" valueKey="id" placeholder="Select vehicle..."
            onChange={(val) => {
              const v = involvedVehicleOptions.find((o: any) => o.id === val);
              setForm(f => ({ ...f, vehicleId: val, vehicleName: v?.name || '' }));
            }} />
        </div>
        <div className="form-group">
          <label><b>Notes</b></label>
          <textarea className="form-control" value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} />
        </div>
        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <label className="mandatory"><b>At Fault?</b></label>
              <div className="radio-group">
                <label><input type="radio" checked={form.atFault === '1'} onChange={() => setForm(f => ({ ...f, atFault: '1' }))} /> Yes</label>
                <label><input type="radio" checked={form.atFault === '2'} onChange={() => setForm(f => ({ ...f, atFault: '2' }))} /> No</label>
                <label><input type="radio" checked={form.atFault === '3'} onChange={() => setForm(f => ({ ...f, atFault: '3' }))} /> N/A</label>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label className="mandatory"><b>Injured?</b></label>
              <div className="radio-group">
                <label><input type="radio" checked={form.injured === '1'} onChange={() => setForm(f => ({ ...f, injured: '1' }))} /> Yes</label>
                <label><input type="radio" checked={form.injured === '2'} onChange={() => setForm(f => ({ ...f, injured: '2' }))} /> No</label>
                <label><input type="radio" checked={form.injured === '3'} onChange={() => setForm(f => ({ ...f, injured: '3' }))} /> N/A</label>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label className="mandatory"><b>Veh Dmg?</b></label>
              <div className="radio-group">
                <label><input type="radio" checked={form.vehDmg === '1'} onChange={() => setForm(f => ({ ...f, vehDmg: '1' }))} /> Yes</label>
                <label><input type="radio" checked={form.vehDmg === '2'} onChange={() => setForm(f => ({ ...f, vehDmg: '2' }))} /> No</label>
                <label><input type="radio" checked={form.vehDmg === '3'} onChange={() => setForm(f => ({ ...f, vehDmg: '3' }))} /> N/A</label>
              </div>
            </div>
          </div>
        </div>
      </ConfirmModal>
    </>
  );
});
